import { formatPaise, GST_STATES, NIL_SUPPLY_TYPES, type NilSupplyType, readRupees } from '@oneapp/gst';
import type { ReturnDraft } from '@oneapp/shared';
import { useQuery } from '@tanstack/react-query';
import { type FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Callout } from '../components/ui';
import { ApiError } from '../lib/api';
import { salesApi, salesKeys } from './api';
import { TextRow } from './SaleForm';

/**
 * K6 · The month's GST-free sales (GSTR-1 Table 8, gap 4): nil-rated, exempt and non-GST sales as the
 * person gives them. Most go to consumers in their own state, so that's one set of boxes; "Split it" shows
 * Table 8's four rows. The nil-rated part of the 0% bills is counted on its own, and shown.
 */

type Kind = 'nil' | 'exempt' | 'nonGst';
type Amounts = Record<Kind, string>;
const KINDS: Kind[] = ['exempt', 'nil', 'nonGst'];
const SIMPLE: NilSupplyType = 'INTRAB2C';
const blank = (): Amounts => ({ nil: '', exempt: '', nonGst: '' });
const rupees = (paise: number) => (paise ? (paise / 100).toLocaleString('en-IN', { maximumFractionDigits: 2 }) : '');

export interface GstFreeFormProps {
  returnId: string;
  draft: ReturnDraft;
  sellerGstin: string;
  formId: string;
  onBusy?: (busy: boolean) => void;
  onSaved: (draft: ReturnDraft) => void;
}

export function GstFreeForm({ returnId, sellerGstin, formId, onBusy, onSaved }: GstFreeFormProps) {
  const { t } = useTranslation();
  const query = useQuery({
    queryKey: salesKeys.gstFree(returnId),
    queryFn: ({ signal }) => salesApi.gstFree(returnId, signal),
    staleTime: 0,
  });
  const [rows, setRows] = useState<Record<NilSupplyType, Amounts> | null>(null);
  const [split, setSplit] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const gstFree = query.data?.gstFree;
  useEffect(() => {
    if (!gstFree || rows) return;
    const start = Object.fromEntries(NIL_SUPPLY_TYPES.map((type) => [type, blank()])) as Record<NilSupplyType, Amounts>;
    for (const row of gstFree.rows) {
      start[row.supplyType] = { nil: rupees(row.nil), exempt: rupees(row.exempt), nonGst: rupees(row.nonGst) };
    }
    setRows(start);
    setSplit(gstFree.rows.some((row) => row.supplyType !== SIMPLE));
  }, [gstFree, rows]);

  const state = GST_STATES[sellerGstin.slice(0, 2)] ?? '';
  if (query.isPending || (gstFree && !rows)) return <p className="muted">{t('sales.loading')}</p>;
  if (!gstFree || !rows) {
    return (
      <Callout tone="error" icon="alert-triangle">
        {query.error instanceof ApiError && query.error.status === 404 ? t('sales.notFound') : t('sales.loadFailed')}
      </Callout>
    );
  }

  const amountOf = (text: string) => (text.trim() ? readRupees(text) : 0);
  const types = split ? NIL_SUPPLY_TYPES : [SIMPLE];
  const fromBills = gstFree.fromBills.reduce((n, r) => n + r.nil, 0);
  const total = { nil: fromBills, exempt: 0, nonGst: 0 };
  for (const type of types) {
    for (const kind of KINDS) total[kind] += amountOf(rows[type][kind]) ?? 0;
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!rows) return;
    const found: Record<string, string> = {};
    const body = types.map((supplyType) => {
      const row = { supplyType, nil: 0, exempt: 0, nonGst: 0 };
      for (const kind of KINDS) {
        const value = amountOf((rows[supplyType] as Amounts)[kind]);
        if (value === null) found[`${supplyType}.${kind}`] = t('sales.form.errors.amount');
        else row[kind] = value;
      }
      return row;
    });
    setErrors(found);
    if (Object.keys(found).length) return;
    onBusy?.(true);
    setProblem(null);
    try {
      const result = await salesApi.putGstFree(returnId, {
        rows: body.filter((r) => r.nil || r.exempt || r.nonGst),
        ...(gstFree?.sellsGstFree === null ? { sellsGstFree: true } : {}),
      });
      onSaved(result.return);
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      setProblem(error.problem?.detail ?? t('sales.gstFree.failed'));
    } finally {
      onBusy?.(false);
    }
  }

  const set = (type: NilSupplyType, kind: Kind, value: string) =>
    setRows((r) => (r ? { ...r, [type]: { ...r[type], [kind]: value } } : r));

  return (
    <form id={formId} className="sale-form gst-free" noValidate onSubmit={submit}>
      <h2 className="sale-form__title">{t('sales.gstFree.title')}</h2>
      <p className="muted">{t('sales.gstFree.lead')}</p>
      {fromBills ? (
        <Callout tone="green" icon="check-circle">
          {t('sales.gstFree.fromBills', { amount: formatPaise(fromBills) })}
        </Callout>
      ) : null}
      {types.map((type) => (
        <fieldset key={type} className="gst-free__row">
          {split ? <legend className="field__label">{t(`sales.gstFree.rows.${type}`, { state })}</legend> : null}
          <div className="fields-row">
            {KINDS.map((kind) => (
              <TextRow
                key={kind}
                label={t(`sales.gstFree.${kind === 'nil' ? (fromBills ? 'moreNil' : 'nil') : kind}`)}
                value={rows[type][kind]}
                error={errors[`${type}.${kind}`]}
                onChange={(v) => set(type, kind, v)}
                inputMode="text"
                placeholder="0"
              />
            ))}
          </div>
        </fieldset>
      ))}
      {!split ? (
        <div className="gst-free__where">
          <span>
            <strong>{t('sales.gstFree.allLocal', { state })}</strong>
            <span className="muted"> {t('sales.gstFree.allLocalHint')}</span>
          </span>
          <button type="button" className="linkbtn" onClick={() => setSplit(true)}>
            {t('sales.gstFree.split')}
          </button>
        </div>
      ) : null}
      <div className="sale-preview" aria-live="polite">
        <span className="sale-preview__route">{t('sales.gstFree.table8')}</span>
        {KINDS.map((kind) =>
          total[kind] ? (
            <span key={kind} className="sale-preview__row">
              <span>{t(`sales.gstFree.${kind}`)}</span>
              <span className="sale-preview__value">{formatPaise(total[kind])}</span>
            </span>
          ) : null,
        )}
        <span className="sale-preview__row sale-preview__row--total">
          <span>{t('sales.gstFree.total')}</span>
          <span className="sale-preview__total">{formatPaise(total.nil + total.exempt + total.nonGst)}</span>
        </span>
      </div>
      <p className="field__help">{t('sales.gstFree.gstr3b')}</p>
      {problem ? (
        <Callout tone="error" icon="alert-triangle" role="alert">
          {problem}
        </Callout>
      ) : null}
    </form>
  );
}
